import { ChipInput } from '@/components/ChipInput'
import { InfoTooltip } from '@/components/InfoTooltip'
import type { GlobalDefaultsNormalisation } from '@/types/bundle'

export interface NormalisationTabProps {
  normalisation: GlobalDefaultsNormalisation
  onChange: (normalisation: GlobalDefaultsNormalisation) => void
}

// Shape confirmed against real bundle fixture data - the schema itself
// leaves this block unconstrained ({"type": "object"}), same as
// document_types' own `defaults`.
export function NormalisationTab({ normalisation, onChange }: NormalisationTabProps) {
  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-4 rounded-control border border-border bg-surface p-4">
        <h2 className="flex items-center gap-1 text-header font-semibold text-text-primary">
          Currency &amp; numbers
          <InfoTooltip text="How the Normalisation Engine recognises money and number values in extracted text." />
        </h2>

        <div className="flex flex-col gap-1">
          <label className="flex items-center gap-1 text-meta font-medium text-text-secondary">
            Currency symbols
            <InfoTooltip text="Symbols recognised as a currency marker (e.g. £, $, €) when normalising a money field." />
          </label>
          <ChipInput
            value={normalisation.currency_symbols ?? []}
            onChange={(currency_symbols) => onChange({ ...normalisation, currency_symbols })}
            placeholder="Type a symbol, press Enter…"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="flex items-center gap-1 text-meta font-medium text-text-secondary">
            Thousands separators
            <InfoTooltip text="Characters treated as a thousands separator (e.g. a comma in 1,234.56) and stripped before parsing a number." />
          </label>
          <ChipInput
            value={normalisation.thousands_separators ?? []}
            onChange={(thousands_separators) => onChange({ ...normalisation, thousands_separators })}
            placeholder="Type a character, press Enter…"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-meta font-medium text-text-secondary" htmlFor="decimal-separator">
            Decimal separator
          </label>
          <input
            id="decimal-separator"
            type="text"
            maxLength={1}
            value={normalisation.decimal_separator ?? ''}
            onChange={(event) => onChange({ ...normalisation, decimal_separator: event.target.value || undefined })}
            className="w-24 rounded-control border border-border bg-bg px-3 py-2 text-body text-text-primary outline-none"
          />
        </div>
      </section>

      <section className="flex flex-col gap-4 rounded-control border border-border bg-surface p-4">
        <h2 className="flex items-center gap-1 text-header font-semibold text-text-primary">
          Dates
          <InfoTooltip text="strptime-style date formats the Normalisation Engine tries, in order, when parsing a date field." />
        </h2>

        <div className="flex flex-col gap-1">
          <label className="flex items-center gap-1 text-meta font-medium text-text-secondary">
            Date formats
            <InfoTooltip text="Tried first, in order, against every date field's extracted text." />
          </label>
          <ChipInput
            value={normalisation.date_formats ?? []}
            onChange={(date_formats) => onChange({ ...normalisation, date_formats })}
            placeholder="e.g. %d/%m/%Y, press Enter…"
            reorderable
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="flex items-center gap-1 text-meta font-medium text-text-secondary">
            Fallback date formats
            <InfoTooltip text="Tried only if none of the formats above match - a wider net for less common date spellings." />
          </label>
          <ChipInput
            value={normalisation.date_formats_fallback ?? []}
            onChange={(date_formats_fallback) => onChange({ ...normalisation, date_formats_fallback })}
            placeholder="e.g. %d-%b-%Y, press Enter…"
            reorderable
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="flex items-center gap-1 text-meta font-medium text-text-secondary" htmlFor="date-output-format">
            Date output format
            <InfoTooltip text="How a parsed date is rendered in the field's final output value (a %-style strftime pattern, e.g. %d/%m/%Y) - different from the parsing formats above, which only recognise a date, not display it. Applies platform-wide only; not client-overridable. Leave blank to default to ISO-8601 (YYYY-MM-DD)." />
          </label>
          <input
            id="date-output-format"
            type="text"
            value={normalisation.date_output_format ?? ''}
            onChange={(event) => onChange({ ...normalisation, date_output_format: event.target.value || undefined })}
            placeholder="e.g. %d/%m/%Y"
            className="w-40 rounded-control border border-border bg-bg px-3 py-2 text-body text-text-primary outline-none"
          />
        </div>
      </section>

      <section className="flex flex-col gap-4 rounded-control border border-border bg-surface p-4">
        <h2 className="flex items-center gap-1 text-header font-semibold text-text-primary">
          Booleans
          <InfoTooltip text="Words recognised as true/false when normalising a boolean field (matched case-insensitively)." />
        </h2>

        <div className="flex flex-col gap-1">
          <label className="text-meta font-medium text-text-secondary">Recognised as true</label>
          <ChipInput
            value={normalisation.boolean_true ?? []}
            onChange={(boolean_true) => onChange({ ...normalisation, boolean_true })}
            placeholder="Type a word, press Enter…"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-meta font-medium text-text-secondary">Recognised as false</label>
          <ChipInput
            value={normalisation.boolean_false ?? []}
            onChange={(boolean_false) => onChange({ ...normalisation, boolean_false })}
            placeholder="Type a word, press Enter…"
          />
        </div>
      </section>
    </div>
  )
}
